import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '../stores/authStore'
import { redirectToLogin } from '../utils/authNavigation'

const BASE_URL = '/api'

class ApiService {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      baseURL: BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.instance.interceptors.request.use(
      (config) => {
        const token = useAuthStore.getState().token
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        const requestUrl = String(error.config?.url || '')
        const isLoginRequest = requestUrl.includes('/auth/login')
        if (error.response?.status === 401 && !isLoginRequest) {
          useAuthStore.getState().logout()
          redirectToLogin()
        }
        return Promise.reject(error)
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.get(url, config)
    return response.data
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.post(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.put(url, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.delete(url, config)
    return response.data
  }

  async upload<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.post(url, formData, {
      ...config,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }
}

export const apiService = new ApiService()
export const modelUsageApi = {
  summary: (params: any) => apiService.get<any>('/model-usage/summary', {params}),
  calls: (params: any) => apiService.get<any>('/model-usage/calls', {params}),
  saveBill: (data: any) => apiService.put<any>('/model-usage/daily-bills', data),
  savePrice: (data: any) => apiService.put<any>('/model-usage/prices', data),
  saveAlerts: (data: any) => apiService.put<any>('/model-usage/alerts', data),
}

export const authApi = {
  login: (data: { username: string; password: string }) =>
    apiService.post('/auth/login', data),
  logout: () => apiService.post('/auth/logout'),
  me: () => apiService.get('/auth/me'),
  updateProfile: (data: { email?: string; currentPassword?: string; newPassword?: string }) =>
    apiService.put('/auth/profile', data),
  uploadAvatar: (formData: FormData) =>
    apiService.upload('/auth/avatar', formData),
}

export const conversationApi = {
  list: (params?: any) => apiService.get('/conversations', { params }),
  get: (id: string) => apiService.get(`/conversations/${id}`),
  getByConversationId: (conversationId: string) => apiService.get(`/conversations/detail/${conversationId}`),
  getCustomerHistory: (conversationId: string) => apiService.get(`/conversations/detail/${conversationId}/customer-history`),
  resolveReference: (conversationId?: string, dbId?: string) =>
    apiService.get('/conversations/resolve-reference', { params: { conversationId, dbId } }),
  fetch: (data: any) => apiService.post('/conversations/fetch', data),
  stats: () => apiService.get('/conversations/stats'),
  tags: () => apiService.get('/conversations/tags'),
  categoryTree: () => apiService.get('/conversations/category-tree'),
  fixEncoding: () => apiService.post('/conversations/fix-encoding', {}, { timeout: 300000 }),
  fetchedDates: () => apiService.get('/conversations/fetched-dates'),
  classify: () => apiService.post('/conversations/classify'),
  categoryStats: () => apiService.get('/conversations/category-stats'),
  filteredIds: (params?: any) => apiService.get('/conversations/filtered-ids', { params }),
  deleteSingle: (id: string) => apiService.delete(`/conversations/${id}`),
  batchDeletePreview: (ids: string[]) => apiService.post('/conversations/batch-delete-preview', { ids }),
  batchDelete: (ids: string[]) => apiService.post('/conversations/batch-delete', { ids }),
}

export const analysisApi = {
  list: (params?: any, config?: any) => apiService.get('/analysis', { params, ...config }),
  insightItems: (params?: any, config?: any) => apiService.get('/analysis/insight-items', { params, ...config }),
  reviewListLite: (params?: any) => apiService.get('/analysis/review-list-lite', { params }),
  get: (id: string) => apiService.get(`/analysis/${id}`),
  trigger: (conversationId: string) => apiService.post(`/analysis/trigger/${conversationId}`),
  batch: (options?: any) => apiService.post('/analysis/batch', options),
  dateStatus: () => apiService.get('/analysis/date-status'),
  pendingCount: () => apiService.get('/analysis/pending-count'),
  statusSummary: () => apiService.get('/analysis/status-summary'),
  reviewList: (params?: any) => apiService.get('/analysis/review-list', { params }),
  getConfig: () => apiService.get('/analysis/config'),
  updateConfig: (data: { mode?: string; subMode?: string; concurrency?: number }) => apiService.post('/analysis/config', data),
}

export const statisticsApi = {
  dashboardSummary: (params?: any) => apiService.get('/statistics/dashboard-summary', { params }),
  overview: (params?: any) => apiService.get('/statistics/overview', { params }),
  trend: (params?: any) => apiService.get('/statistics/trend', { params }),
  distribution: (params?: any) => apiService.get('/statistics/distribution', { params }),
  categoryDistribution: (params?: any) => apiService.get('/statistics/category-distribution', { params }),
  moduleDistribution: (params?: any) => apiService.get('/statistics/module-distribution', { params }),
}

export const reportApi = {
  retentionCoverage: (params?: any) => apiService.post('/reports/retention-coverage', params, { timeout: 120000 }),
  cancel: (id: string) => apiService.post(`/reports/${id}/cancel`),
  generate: (type: string, params?: any) => apiService.post(`/reports/generate/${type}`, params),
  list: () => apiService.get('/reports'),
  getById: (id: string) => apiService.get(`/reports/${id}`),
  createShare: (id: string) => apiService.post(`/reports/${id}/share`),
  revokeShare: (id: string) => apiService.delete(`/reports/${id}/share`),
  delete: (id: string) => apiService.delete(`/reports/${id}`),
}

export const qualityApi = {
  staffList: () => apiService.get('/quality/staff-list'),
  summary: (params?: any) => apiService.get('/quality/summary', { params }),
  items: (params?: any) => apiService.get('/quality/items', { params }),
  servicePoliteness: (params?: any) => apiService.get('/quality/service-politeness', { params }),
  missedReplies: (params?: any) => apiService.get('/quality/missed-replies', { params }),
  review: (id: string, data: any) => apiService.post(`/quality/review/${id}`, data),
  outlier: (params?: any) => apiService.get('/quality/outlier', { params }),
}

export const chatApi = {
  send: (data: { message: string; days: number }) => apiService.post('/chat/send', data),
  history: () => apiService.get('/chat/history'),
  staffList: (days: number) => apiService.get('/chat/staff-list', { params: { days } }),
}

export const smartChatApi = {
  generateReport: (data: { context: string }) => apiService.post('/chat/generate-report', data),
  history: () => apiService.get('/chat/smart-history'),
}

export const chatHistoryApi = {
  list: (params?: any) => apiService.get('/chat/histories', { params }),
  get: (id: string) => apiService.get(`/chat/histories/${id}`),
  delete: (id: string) => apiService.delete(`/chat/histories/${id}`),
  batchDelete: (ids: string[]) => apiService.post('/chat/histories/batch-delete', { ids }),
}

export const configApi = {
  list: () => apiService.get('/configs'),
  get: (key: string) => apiService.get(`/configs/${key}`),
  update: (key: string, value: any) => apiService.put(`/configs/${key}`, { value }),
  testQiyu: () => apiService.post('/configs/test-qiyu'),
  testFeishu: () => apiService.post('/configs/test-feishu'),
  testModel: (provider: string) => apiService.post(`/configs/test-model/${provider}`),
  listModels: (provider: string) => apiService.get(`/configs/list-models/${provider}`),
  listPrompts: () => apiService.get('/configs/prompts'),
  getPrompt: (agentType: string) => apiService.get(`/configs/prompt/${agentType}`),
  updatePrompt: (agentType: string, data: { systemPrompt: string; userPrompt: string }) => apiService.put(`/configs/prompt/${agentType}`, data),
  resetPrompt: (agentType: string) => apiService.delete(`/configs/prompt/${agentType}`),
  resetAllPrompts: () => apiService.delete('/configs/prompts'),
  getBackup: () => apiService.get('/configs/backup'),
  updateBackup: (value: any) => apiService.put('/configs/backup', value),
  triggerBackup: () => apiService.post('/configs/backup/trigger'),
  listBackups: () => apiService.get('/configs/backup/list'),
  restoreBackup: (filename: string) => apiService.post('/configs/backup/restore', { filename }),
  getScheduler: () => apiService.get('/configs/scheduler'),
  updateScheduler: (value: any) => apiService.put('/configs/scheduler', { value }),
}

export const taskApi = {
  list: (params?: any) => apiService.get('/tasks', { params }),
  get: (id: string) => apiService.get(`/tasks/${id}`),
  cancel: (id: string) => apiService.post(`/tasks/cancel/${id}`),
  retry: (id: string) => apiService.post(`/tasks/retry/${id}`),
  batchRetry: (ids: string[]) => apiService.post('/tasks/batch-retry', { ids }),
  delete: (id: string) => apiService.delete(`/tasks/${id}`),
}

export const userApi = {
  list: (params?: { page?: number; pageSize?: number }) => apiService.get('/users', { params }),
  create: (data: { username: string; email?: string; password: string; roleId: string }) =>
    apiService.post('/users', data),
  update: (id: string, data: { email?: string; roleId?: string; status?: string }) =>
    apiService.put(`/users/${id}`, data),
  delete: (id: string) => apiService.delete(`/users/${id}`),
  resetPassword: (id: string, newPassword: string) =>
    apiService.put(`/users/${id}/reset-password`, { newPassword }),
}

export const roleApi = {
  list: () => apiService.get('/roles'),
  get: (id: string) => apiService.get(`/roles/${id}`),
  getAllPermissions: () => apiService.get('/roles/permissions'),
  create: (data: { name: string; displayName: string; permissions: string[] }) =>
    apiService.post('/roles', data),
  update: (id: string, data: { displayName?: string; permissions?: string[] }) =>
    apiService.put(`/roles/${id}`, data),
  delete: (id: string) => apiService.delete(`/roles/${id}`),
}
